import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import PrimeModal from '@/components/primeModal';
import { addDomain, addDict, editDict } from '@/api/dictionary';
import _ from 'lodash';

interface AddKnowledgeProps {
    onSuccess?: (type: 'domain' | 'base', name: String, domainId: String) => void;
    isEdit?: Boolean;
    editContent?: { dictName: String, desc: String }
}

const AddKnowledge = forwardRef<any, AddKnowledgeProps>(({ onSuccess, isEdit = false, editContent }, ref) => {
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState<'domain' | 'base'>('domain');
    const [domainIdValue, setDomainIdValue] = useState('');
    const [moduleTypeValue, setModuleTypeValue] = useState(1);
    const [dictTypeValue, setDictTypeValue] = useState(1);
    const [dictIdValue, setDictIdValue] = useState(531);
    const { t } = useTranslation();

    useImperativeHandle(ref, () => ({
        open: (params) => {
            setType(params.type);
            if (params.domainId) {
                setDomainIdValue(params.domainId)
            }
            if (params.moduleType) {
                setModuleTypeValue(params.moduleType)
            }
            if (params.dictType) {
                setDictTypeValue(params.dictType)
            }
            if (params.dictId) {
                setDictIdValue(params.dictId)
            }
            setVisible(true);
            form.resetFields();
        },
    }));

    const handleOk = _.throttle(async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const method = isEdit ? editDict : type === 'domain' ? addDomain : addDict
            const params = isEdit ? { ...values, name: values.dictName, domainId: domainIdValue, dictId: dictIdValue } :
                type === 'domain' ? { ...values, moduleType: moduleTypeValue } :
                    { ...values, domainId: domainIdValue, moduleType: moduleTypeValue, dictType: dictTypeValue }
            await method(params)

            message.success(t('ikg.success', { action: isEdit ? t('ikg.edit') : t('ikg.add') }));
            setVisible(false);
            onSuccess?.(type, values.dictName, domainIdValue);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }, 2000);

    const handleCancel = () => {
        setVisible(false);
        form.resetFields();
    };

    const validateName = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error(t('ikg.input', { name: t('dict.name') })));
        }
        const regex = /^[\u4e00-\u9fa5a-zA-Z0-9_]+$/;
        if (!regex.test(value)) {
            return Promise.reject(new Error(`${t('knowledge.regName')}`));
        }
        return Promise.resolve();
    };

    return (
        <PrimeModal
            title={type === 'domain' ? t('dict.createKnowledgeDomain') : isEdit ? t('dict.editKnowledgeBase') : t('dict.createKnowledgeBase')}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ ...editContent }}
            >
                <Form.Item
                    name={type === 'domain' ? 'name' : 'dictName'}
                    label={t('dict.name')}
                    rules={[{ validator: validateName, required: true }]}
                >
                    <Input
                        placeholder={t('ikg.input', { name: '' })}
                        maxLength={20}
                    />
                </Form.Item>
                {type === 'base' && (
                    <Form.Item
                        name="desc"
                        label={t('dict.desc')}
                    >
                        <Input.TextArea
                            placeholder={t('ikg.input', { name: '' })}
                            rows={4}
                            maxLength={2000}
                        />
                    </Form.Item>
                )}
            </Form>
        </PrimeModal>
    );
});

export default AddKnowledge; 